import { Link } from 'react-router-dom';
import { ProductMark, PRODUCT_NAME } from './Brand.jsx';
import ExternalLink from './ExternalLink.jsx';
import AppSwitcher from './AppSwitcher.jsx';
import UserMenu from './UserMenu.jsx';
import { BRAND } from '../lib/brand.js';

/**
 * The onGuard lockup: the A2 Pulse mark at 32 px and the product name, linking
 * to the app's landing route. The IPC Global logo is not in the header: it
 * lives in the footer ("onGuard by IPC Global") and in the app switcher.
 * Shared by the workbench TopBar and the onboarding wizard's header.
 */
export function TopBarBrand({ productTo = '/' }) {
  return (
    <Link to={productTo} className="tap flex min-w-0 items-center gap-2.5 rounded text-sm font-semibold tracking-tight text-white sm:text-base">
      <ProductMark tone="light" size={32} />
      <span className="truncate">{PRODUCT_NAME}</span>
    </Link>
  );
}

/**
 * The thin navy-to-blue gradient under a header (spec: the gradient appears
 * only as a thin accent, never behind text). One definition for the workbench
 * TopBar and the onboarding wizard's header.
 */
export function HeaderAccent() {
  return <div data-accent aria-hidden="true" className="h-0.5 bg-gradient-to-r from-ipc-primary to-ipc-light" />;
}

/**
 * The workbench top bar (portal workbench spec), in the universal console
 * order. Left to right: the app switcher, the onGuard lockup, the customer's
 * logo (only when the customer approved it) and the customer switcher. Right: Quick find (an icon on a phone), help
 * and the user menu. DOM order matches the visual order. Below 768 px the switchers and help move into the drawer
 * (AppShell's NavDrawer), which the menu button opens.
 */
export default function TopBar({ productTo, menuOpen, menuButtonRef, onToggleMenu, switcher, quickFind, customerLogo, isOperator, profile, onSignOut }) {
  return (
    <header className="on-dark bg-ipc-primary text-white">
      <div className="flex h-14 items-center gap-3 px-4">
        <button
          ref={menuButtonRef}
          type="button"
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls="nav-drawer"
          onClick={onToggleMenu}
          className="-ml-2 rounded p-2 md:hidden"
        >
          <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        <div className="hidden md:block">
          <AppSwitcher isOperator={isOperator} />
        </div>
        <TopBarBrand productTo={productTo} />
        {(switcher || customerLogo) && (
          <div className="hidden min-w-0 items-center gap-3 md:ml-1 md:flex">
            <span aria-hidden="true" data-topbar-separator="" className="mx-1.5 h-6 w-px bg-white/20" />
            {customerLogo}
            {switcher}
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          {quickFind}
          <ExternalLink href={BRAND.supportUrl} className="tap hidden items-center rounded px-2 py-1 text-sm text-white hover:bg-white/10 md:inline-flex">
            Help
          </ExternalLink>
          <UserMenu profile={profile} onSignOut={onSignOut} />
        </div>
      </div>
      <HeaderAccent />
    </header>
  );
}
